import { ErrorInfo, RefObject, lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient } from '@tanstack/react-query';

import {
  DocumentScanner,
  Message,
  RouteOutlined,
  LocalGasStation,
  Logout,
  GpsFixed,
  Domain,
  AccountCircle,
  InfoOutlined,
  MoreHoriz,
} from '@mui/icons-material';
import { Badge } from '@mui/material';

import { PopupType } from './types';
import type {
  ContactsPopup,
  MessagesPopup,
  More,
  TrackingPopup,
  RoutesPopup,
  RefuelsPopup,
  ProfilePopup,
  SysinfoPopup,
} from './components';
import Error from '@/components/common/Error';
import ReportPopup from './components/report/ReportPopup';

const TrackingPopupLazy = lazy(
  () => import('@/components/tracking/TrackingPopup') as Promise<{ default: typeof TrackingPopup }>
);
const RoutesPopupLazy = lazy(() => import('@/components/routes/RoutesPopup')) as typeof RoutesPopup;
const MessagesPopupLazy = lazy(() => import('@/components/messages/MessagesPopup')) as typeof MessagesPopup;
const RefuelsPopupLazy = lazy(() => import('@/components/refuels/RefuelsPopup')) as typeof RefuelsPopup;
const MoreLazy = lazy(() => import('@/components/More')) as typeof More;
const ContactsPopupLazy = lazy(() => import('@/components/contacts/ContactsPopup')) as typeof ContactsPopup;
const ProfilePopupLazy = lazy(() => import('@/components/profile/ProfilePopup')) as typeof ProfilePopup;
const SysinfoPopupLazy = lazy(() => import('@/components/sysinfo/SysinfoPopup')) as typeof SysinfoPopup;
const ReportPopupLazy = lazy(() => import('@/components/report/ReportPopup')) as typeof ReportPopup;

const newMessageCount = 0;

export function getAdditionalElements(close: () => void, isBottom: boolean) {
  const additionalElements = [
    {
      title: 'mainMenu.contacts',
      icon: <Domain fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ContactsPopupLazy close={close} />,
      ref: undefined,
      showToken: true,
      mainFunction: false,
      showMobile: true,
    },
    {
      title: 'mainMenu.profile',
      icon: <AccountCircle fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ProfilePopupLazy close={close} />,
      ref: undefined,
      showToken: false,
      mainFunction: false,
      showMobile: true,
    },
    {
      title: 'mainMenu.systemInformation',
      icon: <InfoOutlined fontSize={isBottom ? 'small' : 'medium'} />,
      element: <SysinfoPopupLazy close={close} />,
      ref: undefined,
      showToken: true,
      mainFunction: false,
      showMobile: true,
    },
    {
      title: 'mainMenu.logout',
      icon: <Logout fontSize={isBottom ? 'small' : 'medium'} />,
      element: undefined,
      ref: undefined,
      showToken: false,
      mainFunction: false,
      showMobile: true,
    },
  ];

  return additionalElements;
}

export function popupDefs({
  close,
  logout,
  ref,
  client,
  isBottom,
}: {
  close: () => void;
  logout: () => void;
  ref: RefObject<HTMLAnchorElement | null>;
  client: QueryClient;
  isBottom: boolean;
}): PopupType[] {
  function logError(error: Error, info: ErrorInfo) {
    console.log(error.message);
    console.log(info);
  }

  // FIXME ez igy nem lazy loading ?
  const arr: PopupType[] = [
    {
      title: 'mainMenu.tracking',
      icon: <GpsFixed fontSize={isBottom ? 'small' : 'medium'} />,
      element: <TrackingPopupLazy data-qa-element-id="tracking-popup" />,
      ref: undefined,
      showToken: true,
      mainFunction: true,
      showMobile: true,
    },
    {
      title: 'mainMenu.routes',
      icon: <RouteOutlined fontSize={isBottom ? 'small' : 'medium'} sx={{ transform: 'rotate(90deg)' }} />,
      element: (
        <ErrorBoundary FallbackComponent={Error} onError={logError} onReset={() => client.clear()}>
          <RoutesPopupLazy close={close} data-qa-element-id="routes-popup" />
        </ErrorBoundary>
      ),
      ref: undefined,
      showToken: false,
      mainFunction: true,
      showMobile: true,
    },
    {
      title: 'mainMenu.messages',
      icon:
        newMessageCount > 0 ? (
          <Badge color="secondary" variant="dot">
            <Message fontSize={isBottom ? 'small' : 'medium'} />
          </Badge>
        ) : (
          <Message fontSize={isBottom ? 'small' : 'medium'} />
        ),
      // FIXME ne a react=query teljes cache-t torolje ? csak a messages ?
      element: (
        <ErrorBoundary FallbackComponent={Error} onError={logError} onReset={() => client.clear()}>
          <MessagesPopupLazy close={close} data-qa-element-id="messages-popup" />
        </ErrorBoundary>
      ),
      ref: undefined,
      showToken: false,
      mainFunction: true,
      showMobile: true,
    },
    {
      title: 'mainMenu.refuels',
      icon: <LocalGasStation fontSize={isBottom ? 'small' : 'medium'} />,
      element: <RefuelsPopupLazy close={close} data-qa-element-id="refuels-popup" />,
      ref: undefined,
      showToken: false,
      mainFunction: true,
      showMobile: true,
    },
    {
      title: 'mainMenu.reports',
      icon: <DocumentScanner fontSize={isBottom ? 'small' : 'medium'} />,
      element: <ReportPopupLazy close={close} data-qa-element-id="reports-popup" />,
      ref: undefined,
      showToken: false,
      mainFunction: true,
      requiredExtraFeature: 'REPORT_TAB',
      showMobile: false,
    },
  ];

  let additionalElement: PopupType[];
  if (isBottom) {
    additionalElement = [
      {
        title: 'mainMenu.more',
        icon: <MoreHoriz fontSize="small" />,
        element: <MoreLazy close={close} ref={ref} logout={logout} data-qa-element-id="more-menu-list" />,
        ref,
        showToken: true,
        mainFunction: false,
        showMobile: true,
      },
    ];
  } else {
    additionalElement = getAdditionalElements(close, isBottom);
  }
  return arr.concat(additionalElement);
}
